/**
 * Scheduling helpers for site content (#20): the runtime's per-item
 * `countdown: { enabled, startsAt }` (UPCOMING badge + countdown until the
 * start, then LIVE / playable), `availability: { from, until }` and calendar
 * `events` (`{ contentId, title, start, end }`, shown on the site's /calendar).
 * Inputs are `<input type="datetime-local">` strings in the browser's zone;
 * the API speaks ISO 8601 UTC.
 */

const pad = (n) => String(n).padStart(2, '0')

/** ISO (or Date) → "YYYY-MM-DDTHH:mm" in the local zone; '' when empty/invalid. */
export function toLocalInput(value) {
	if (!value) return ''
	const d = value instanceof Date ? value : new Date(value)
	if (Number.isNaN(d.getTime())) return ''
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** "YYYY-MM-DDTHH:mm" (local) → ISO UTC string; null when empty/invalid. */
export function fromLocalInput(value) {
	const s = String(value || '').trim()
	if (!s) return null
	const d = new Date(s)
	return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

export function addMinutes(iso, minutes) {
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return null
	return new Date(d.getTime() + Number(minutes || 0) * 60_000).toISOString()
}

/** Whole minutes between two ISO stamps (≥ 0), or `fallback` when either is missing. */
export function minutesBetween(startIso, endIso, fallback = 60) {
	if (!startIso || !endIso) return fallback
	const ms = new Date(endIso).getTime() - new Date(startIso).getTime()
	return Number.isFinite(ms) && ms >= 0 ? Math.round(ms / 60_000) : fallback
}

export function localTimeZone() {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
	} catch {
		return ''
	}
}

export const DURATION_OPTIONS = [
	{ value: 30, text: '30 minutes' },
	{ value: 60, text: '1 hour' },
	{ value: 90, text: '1½ hours' },
	{ value: 120, text: '2 hours' },
	{ value: 180, text: '3 hours' },
	{ value: 240, text: '4 hours' },
	{ value: 480, text: '8 hours' },
	{ value: 1440, text: 'All day (24 hours)' },
]

/**
 * Viewer-facing state of a scheduled item, mirroring the runtime's
 * `lib/live-state.ts`: countdown on + start ahead → upcoming; start passed →
 * live (until `availability.until`, if set) → ended.
 * @returns {'upcoming'|'live'|'ended'|null} null when the item has no schedule
 */
export function scheduleState(item, now = Date.now()) {
	const startsAt = item?.countdown?.enabled ? item.countdown.startsAt : null
	const until = item?.availability?.until
	if (until && new Date(until).getTime() <= now) return 'ended'
	if (!startsAt) return null
	return new Date(startsAt).getTime() > now ? 'upcoming' : 'live'
}

export function fmtDateTime(value) {
	if (!value) return '—'
	const d = new Date(value)
	return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

// --- calendar grid (local zone) --------------------------------------------

/** Local "YYYY-MM-DD" key of a Date / ISO stamp. */
export function dayKey(value) {
	const d = value instanceof Date ? value : new Date(value)
	if (Number.isNaN(d.getTime())) return ''
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Midnight (local) of a "YYYY-MM-DD" key. */
export function dateFromKey(key) {
	const [y, m, d] = String(key).split('-').map(Number)
	return new Date(y, (m || 1) - 1, d || 1)
}

/** Monday-first weekday index (0 = Monday … 6 = Sunday). */
export const weekdayIndex = (date) => (date.getDay() + 6) % 7

/** Monday (local midnight) of the week containing `date`. */
export function startOfWeek(date) {
	const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
	d.setDate(d.getDate() - weekdayIndex(d))
	return d
}

/** The 6 × 7 day grid (Monday first) that shows a whole month. */
export function monthGrid(year, month) {
	const first = startOfWeek(new Date(year, month, 1))
	return Array.from({ length: 42 }, (_, i) => {
		const d = new Date(first.getFullYear(), first.getMonth(), first.getDate() + i)
		return { date: d, key: dayKey(d), inMonth: d.getMonth() === month }
	})
}

/** The 7 days of the week containing `date`. */
export function weekDays(date) {
	const monday = startOfWeek(date)
	return Array.from({ length: 7 }, (_, i) => {
		const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i)
		return { date: d, key: dayKey(d), inMonth: true }
	})
}

/**
 * Move an event to another local day, keeping its time of day and duration.
 * @returns {{ start: string, end: string|null }} ISO stamps
 */
export function moveEventToDay(event, key) {
	const start = new Date(event.start)
	const target = dateFromKey(key)
	const newStart = new Date(target.getFullYear(), target.getMonth(), target.getDate(), start.getHours(), start.getMinutes(), start.getSeconds())
	const duration = event.end ? new Date(event.end).getTime() - start.getTime() : null
	return { start: newStart.toISOString(), end: duration !== null && duration >= 0 ? new Date(newStart.getTime() + duration).toISOString() : null }
}
